"use client";

import { startOfDay } from "date-fns";

import { hotelSearchSchema } from "@/feature/hotel/search/schema/hotel-search-zod.schema";
import { getDefaultHotelFormValue } from "@/feature/hotel/search/lib/hotel-search-defaults";
import type { IHotelsSearchSchema } from "@/feature/hotel/search/types/hotel-search-type";

const HOTEL_SEARCH_SESSION_KEY = "hotel:search:last:v1";

type PersistedHotelSearch = Omit<IHotelsSearchSchema, "date"> & {
  date: {
    from: string;
    to: string;
  };
};

function parseStoredDate(value: string | null | undefined) {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function cloneHotelSearchData(data: IHotelsSearchSchema): IHotelsSearchSchema {
  return {
    location: { ...data.location },
    date: {
      from:
        data.date.from instanceof Date
          ? new Date(data.date.from)
          : new Date(data.date.from),
      to:
        data.date.to instanceof Date
          ? new Date(data.date.to)
          : new Date(data.date.to),
    },
    rooms: data.rooms.map((room) => ({
      adults: room.adults,
      children: room.children,
      infants: room.infants,
      children_ages: [...(room.children_ages ?? [])],
    })),
  };
}

export function createInitialHotelSearchData(
  serverInitialData?: IHotelsSearchSchema | null,
): IHotelsSearchSchema {
  if (serverInitialData) {
    return cloneHotelSearchData(serverInitialData);
  }

  return cloneHotelSearchData(getDefaultHotelFormValue());
}

export function saveLastHotelSearch(data: IHotelsSearchSchema): void {
  if (typeof window === "undefined") return;

  const payload: PersistedHotelSearch = {
    location: { ...data.location },
    date: {
      from:
        data.date.from instanceof Date
          ? data.date.from.toISOString()
          : new Date(data.date.from).toISOString(),
      to:
        data.date.to instanceof Date
          ? data.date.to.toISOString()
          : new Date(data.date.to).toISOString(),
    },
    rooms: data.rooms.map((room) => ({
      adults: room.adults,
      children: room.children,
      infants: room.infants,
      children_ages: [...(room.children_ages ?? [])],
    })),
  };

  try {
    window.sessionStorage.setItem(
      HOTEL_SEARCH_SESSION_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore sessionStorage failures.
  }
}

export function getLastHotelSearch(): IHotelsSearchSchema | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(HOTEL_SEARCH_SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PersistedHotelSearch;
    const fromDate = parseStoredDate(parsed.date?.from);
    const toDate = parseStoredDate(parsed.date?.to);

    if (!fromDate || !toDate) {
      window.sessionStorage.removeItem(HOTEL_SEARCH_SESSION_KEY);
      return null;
    }

    // Reject stored search if check-in is now in the past
    if (fromDate < startOfDay(new Date())) {
      window.sessionStorage.removeItem(HOTEL_SEARCH_SESSION_KEY);
      return null;
    }

    // Reject if check-out is not after check-in
    if (toDate <= fromDate) {
      window.sessionStorage.removeItem(HOTEL_SEARCH_SESSION_KEY);
      return null;
    }

    const candidate: IHotelsSearchSchema = {
      location: { ...parsed.location },
      date: {
        from: fromDate,
        to: toDate,
      },
      rooms: (parsed.rooms ?? []).map((room) => ({
        adults: room.adults,
        children: room.children,
        infants: room.infants,
        children_ages: [...(room.children_ages ?? [])],
      })),
    };

    const result = hotelSearchSchema.safeParse(candidate);
    if (!result.success) {
      window.sessionStorage.removeItem(HOTEL_SEARCH_SESSION_KEY);
      return null;
    }

    return cloneHotelSearchData(result.data);
  } catch {
    window.sessionStorage.removeItem(HOTEL_SEARCH_SESSION_KEY);
    return null;
  }
}

